/**
 * Pointer-based drag & drop in stage coordinates.
 *
 * `useDraggable` moves an element with a CSS translate while the pointer is
 * down and, on release, finds the drop zone under the pointer (registered with
 * `useDropZone`) and calls `onDrop(zoneId)`. If `onDrop` returns false the
 * element animates back to its origin.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent, type RefObject } from "react";
import { PlayerContext } from "./context";
import { playFx } from "./sounds";

/** Stage scale (screen px per stage px), provided by <Stage>. */
export const StageScaleContext = createContext(1);
export const useStageScale = () => useContext(StageScaleContext);

interface Zone {
  id: string;
  el: HTMLElement;
}
const zonesCtx = createContext<{ zones: Map<string, Zone> } | null>(null);
export function DropZonesProvider({ children }: { children: React.ReactNode }) {
  const value = useMemo(() => ({ zones: new Map<string, Zone>() }), []);
  return <zonesCtx.Provider value={value}>{children}</zonesCtx.Provider>;
}

/** Looks up a registered drop zone's element (for effects over it). */
export function useZoneElement() {
  const ctx = useContext(zonesCtx);
  return useCallback((id: string): HTMLElement | null => ctx?.zones.get(id)?.el ?? null, [ctx]);
}

export function useDropZone(id: string, ref: RefObject<HTMLElement | null>, enabled = true) {
  const ctx = useContext(zonesCtx);
  useEffect(() => {
    if (!ctx || !enabled) return;
    const el = ref.current;
    if (!el) return;
    ctx.zones.set(id, { id, el });
    return () => {
      ctx.zones.delete(id);
    };
  }, [ctx, id, ref, enabled]);
}

export interface DragState {
  dragging: boolean;
  dx: number;
  dy: number;
  /** true while animating back after a refused drop */
  returning: boolean;
}

export interface DraggableOptions {
  /** Called with the drop zone id under the pointer (or null). Return true to keep the element where it was dropped. */
  onDrop: (zoneId: string | null, point: { x: number; y: number }) => boolean | void;
  onStart?: () => void;
  onMove?: (point: { x: number; y: number }) => void;
  disabled?: boolean;
}

export function useDraggable(opts: DraggableOptions) {
  const ctx = useContext(zonesCtx);
  const player = useContext(PlayerContext);
  const scale = useStageScale();
  const [state, setState] = useState<DragState>({ dragging: false, dx: 0, dy: 0, returning: false });
  const start = useRef<{ x: number; y: number } | null>(null);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const onPointerDown = useCallback(
    (e: RPointerEvent<HTMLElement>) => {
      if (optsRef.current.disabled) return;
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      start.current = { x: e.clientX, y: e.clientY };
      setState({ dragging: true, dx: 0, dy: 0, returning: false });
      if (player) playFx("take", player.project.settings); // PlayerElement.TakeElement
      optsRef.current.onStart?.();
    },
    [player],
  );

  const onPointerMove = useCallback(
    (e: RPointerEvent<HTMLElement>) => {
      if (!start.current) return;
      const dx = (e.clientX - start.current.x) / scale;
      const dy = (e.clientY - start.current.y) / scale;
      setState({ dragging: true, dx, dy, returning: false });
      optsRef.current.onMove?.({ x: e.clientX, y: e.clientY });
    },
    [scale],
  );

  const onPointerUp = useCallback(
    (e: RPointerEvent<HTMLElement>) => {
      if (!start.current) return;
      start.current = null;
      let zoneId: string | null = null;
      if (ctx) {
        for (const z of ctx.zones.values()) {
          const r = z.el.getBoundingClientRect();
          if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) {
            zoneId = z.id;
            break;
          }
        }
      }
      const keep = optsRef.current.onDrop(zoneId, { x: e.clientX, y: e.clientY });
      if (keep) {
        setState((s) => ({ ...s, dragging: false }));
      } else {
        setState({ dragging: false, dx: 0, dy: 0, returning: true });
        window.setTimeout(() => setState((s) => ({ ...s, returning: false })), 250);
      }
    },
    [ctx],
  );

  const handlers = { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp };
  const style = {
    transform: state.dx || state.dy ? `translate(${state.dx}px, ${state.dy}px)` : undefined,
    transition: state.returning ? "transform .25s ease" : "none",
    zIndex: state.dragging ? 10 : undefined,
    touchAction: "none" as const,
    cursor: opts.disabled ? undefined : state.dragging ? "grabbing" : "grab",
  };
  return { handlers, style, state };
}

/** Pointer position converted to stage coordinates. */
export function toStagePoint(stageEl: HTMLElement, clientX: number, clientY: number, scale: number) {
  const r = stageEl.getBoundingClientRect();
  return { x: (clientX - r.left) / scale, y: (clientY - r.top) / scale };
}
